// frontend/src/components/Header.jsx
//
// ★ 변경사항 (2026):
//   - App.js 탭 버튼(지도/참고/관리/설정)을 헤더 네비 메뉴로 이동
//   - 탭 전환 방식: URL 해시(#search, #support, #manage, #settings) 사용
//     → 예) 지도 클릭 시 /#search 로 이동, App.js 가 해시를 읽어 탭 전환
//   - 매핑: 지도→홈 자리, 참고→매물목록 자리, 관리→단기임대 자리, 설정→유지보수 자리
//   - 건축물 검색은 기존 위치 그대로
//
// 연관 파일:
//   - frontend/src/App.js : 해시 변경 감지 후 tab 상태 변경 처리 필요 (App.js 수정 참고)
//   - 메뉴 추가 시 아래 NAV_ITEMS 배열만 수정하면 됨

import React, { useEffect, useState } from "react";
import "./Header.css";   // [HEADER_MOBILE_V1] 헤더 모바일 대응
import { getMe } from "../api/authApi";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

// ─────────────────────────────────────────────────────────────
// 네비게이션 메뉴 항목 정의
//
//   label  : 화면에 표시되는 메뉴 이름
//   path   : react-router-dom 경로
//   hash   : (선택) URL 해시값 → path="/" 인 항목은 해시로 탭을 구분
//             예) hash: "search" → URL이 /#search 가 됨 → App.js가 탭 전환
//   admin  : true 이면 관리자(grade="admin")에게만 표시
//
// ★ 규칙:
//   - path="/" + hash 있음 → App.js 내 탭 전환 (지도/참고/관리/설정)
//   - path="/building-search" 등 별도 경로 → 해당 페이지로 이동
// ─────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  // ── 전체 공개 ──
  { label: "🗺️ 지도",        path: "/", hash: "search"   },  // App.js tab="search"
  { label: "📚 참고",         path: "/", hash: "support"  },  // App.js tab="support"

  // ── premium 이상 ──
  { label: "📥 접수관리",     path: "/submissions",        roles: ["premium", "admin"] },  // [SUB_MENU_V1]
  { label: "📝 물건접수",     path: "/staff/intake",       roles: ["premium", "admin"] },  // [STAFF_INTAKE_MENU_V1]
  { label: "✍️ 블로그생성",   path: "/blog/create",        roles: ["premium", "admin"] },  // [BLOGPAGE_MENU_V1]
  { label: "📋 계약관리",     path: "/contracts/list",     roles: ["premium", "admin"] },
  { label: "📅 일정관리",     path: "/contracts/calendar", roles: ["premium", "admin"] },
  { label: "🚗 안내내역",     path: "/tour-lists",         roles: ["premium", "admin"] },
  // [GONGSIL_MENU_V1] 공실클럽 수집자료 — 앱 내부 페이지 (components/GongsilView.jsx)
  { label: "🏘️ 수집데이터",   path: "/gongsil",            roles: ["premium", "admin"] },
  { label: "📤 SMS",          path: "/", hash: "settings", roles: ["premium", "admin"] },  // App.js tab="settings"

  // ── admin 전용 ──
  // [REMOVED_V1] { label: "🏢 건축물 검색",  path: "/building-search",    roles: ["admin"] },
  { label: "⚙️ 관리",         path: "/", hash: "manage",   roles: ["admin"] },  // App.js tab="manage"
  // [REMOVED_V1] { label: "🏠 네이버매물",   path: "/naver-admin",        roles: ["admin"] },
  // [REMOVED_V1] { label: "🛡️ DB 관리",     path: "/admin/db",           roles: ["admin"] },
];

export default function Header({ onLoginClick }) {
  const [me, setMe] = useState(null);
  const navigate  = useNavigate();
  const location  = useLocation(); // 현재 URL 경로+해시 감지용

  useEffect(() => {
    getMe()
      .then((res) => {
        setMe(res.user ?? res);
      })
      .catch(() => setMe(null));
  }, []);

  // ─────────────────────────────────────────────────────────
  // 메뉴 클릭 처리
  //
  // ★ 핵심: hash 있는 항목은 window.location.href 로 직접 이동
  //   → navigate()는 React Router 내부 방식이라 hashchange 이벤트가 발생 안 함
  //   → window.location.href 로 바꿔야 App.js의 hashchange 리스너가 감지함
  //
  //   예) "참고" 클릭 → window.location.href = "/#support"
  //       → 브라우저 URL이 /#support 로 바뀜
  //       → App.js의 hashchange 리스너 실행 → setTab("support") → 참고 화면 출력
  // ─────────────────────────────────────────────────────────
  const handleNavClick = (item) => {
    if (item.hash) {
      // 이미 "/" 에 있으면 해시만 바꿈 (hashchange 이벤트 발생)
      // 다른 페이지("/building-search" 등)에서 클릭해도 "/#search" 로 이동하며 정상 동작
      window.location.href = `${item.path}#${item.hash}`;
    } else {
      // 일반 경로("/building-search" 등)는 React Router로 이동
      navigate(item.path);
    }
  };

  // ─────────────────────────────────────────────────────────
  // 활성 메뉴 판별
  //   hash 있는 항목: 현재 경로가 "/" AND 현재 해시가 #해시명 일치
  //     → 지도(#search)는 해시가 없거나 #search 일 때 둘 다 활성으로 처리
  //   hash 없는 항목: 현재 경로가 일치할 때
  // ─────────────────────────────────────────────────────────
  const isActive = (item) => {
    if (item.hash) {
      // path="/" 이고 hash가 있는 메뉴
      const onRoot = location.pathname === "/";
      const hashMatch = location.hash === `#${item.hash}`;
      // 지도(search)는 해시가 아예 없을 때도 기본 활성
      const isDefault = item.hash === "search" && onRoot && !location.hash;
      return onRoot && (hashMatch || isDefault);
    }
    // 일반 경로 메뉴
    if (item.path === "/") return false; // hash 없는 "/" 는 이 함수 위에서 처리됨
    return (
      location.pathname === item.path ||
      location.pathname.startsWith(item.path)
    );
  };

  return (
    // 헤더 전체를 flex column으로 감싸서 [로고행 + 메뉴행] 두 줄 구조
    <header
      style={{
        backgroundColor: "#130869",
        position: "sticky",
        top: 0,
        zIndex: 2000,
        color: "#fff",
      }}
    >
      {/* ── 1행: 로고 + 유저메뉴 ── */}
      <div style={{
        minHeight: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        position: "relative",
      }}>
        {/* 1. 중앙 로고 영역 */}
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          onClick={() => {
            // [LOGO_HOME_V1] 로고 → 홈(지도·리스트 search 탭)
            //  navigate() 는 hashchange 를 안 일으켜 App.js 탭이 안 바뀌던 문제 →
            //  이동 후 hashchange 를 직접 보내 App.js 의 onHashChange 가 tab="search" 로 바꾸게 함
            navigate("/");
            window.scrollTo(0, 0);   // 맨 위로
            setTimeout(() => window.dispatchEvent(new HashChangeEvent("hashchange")), 0);
          }}
        >
          <img
            src="/uploads/모모_매물장_로고.jpg"
            alt="MOMO 로고"
            className="hd-logo-img"
            style={{ height: "45px", width: "auto", maxWidth: "100%", display: "block" }}
            onError={(e) => e.target.style.display = "none"}
          />
        </div>

        {/* 2. 우측 유저 메뉴 영역 */}
        <div className="hd-user" style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexShrink: 0
        }}>
          {me ? (
            <>
              {/* [REMOVED_V1] 테이블복사·DB백업 버튼 제거 (adminDb 라우트 폐기) */}

              {/* 유저 이름/메일 */}
              <span
                style={{
                  fontSize: "14px",
                  cursor: "pointer",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
                onClick={() => navigate("/me")}
              >
                {me.name || me.email}
              </span>{/* [HEADER_MOBILE_V1] 좁은 화면에서는 CSS 로 폭 제한 */}

              {/* 로그아웃 */}
              <button
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.4)",
                  color: "#fff",
                  padding: "3px 8px",
                  borderRadius: "4px",
                  fontSize: "13px"
                }}
                onClick={() => {
                  localStorage.removeItem("token");
                  window.location.reload();
                }}
              >
                로그아웃
              </button>
            </>
          ) : (
            <button
              style={{
                background: "transparent",
                border: "1px solid #fff",
                color: "#fff",
                padding: "5px 12px",
                borderRadius: "4px",
                cursor: "pointer"
              }}
              onClick={onLoginClick}
            >
              로그인/가입
            </button>
          )}
        </div>
      </div>{/* 1행 끝 */}

      {/* ── 2행: 네비게이션 메뉴 ──
          ★ App.js 탭 버튼(지도/참고/관리/설정)이 여기로 이동됨
          ★ 클릭 시 /#search, /#support 등으로 이동 → App.js가 해시 감지 후 탭 전환
      */}
      <nav style={{
        backgroundColor: "rgba(0,0,0,0.25)",
        borderTop: "1px solid rgba(255,255,255,0.12)",
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        overflowX: "auto",    // 모바일에서 메뉴 많으면 가로 스크롤
        gap: "2px",
      }}>
        {NAV_ITEMS
          // 관리자 전용 메뉴는 admin 등급일 때만 표시
          // ── 등급별 메뉴 노출 필터 (2026) ──
          //   roles 없음        → 누구나 표시 (비로그인 포함)
          //   roles 있음        → me.grade 가 배열에 있을 때만 표시
          //   admin:true (구방식) → 하위호환 유지
          //   ※ 메뉴 숨김은 보안이 아님. 실제 차단은 백엔드 등급검사가 담당
          .filter(item => {
            if (item.admin) return me?.grade === "admin";
            if (!item.roles) return true;
            return me ? item.roles.includes(me.grade) : false;
          })
          .map(item => {
            const active = isActive(item);
            return (
              <button
                key={`${item.path}-${item.hash || ""}`}
                onClick={() => handleNavClick(item)}
                style={{
                  background    : active ? "rgba(255,255,255,0.18)" : "transparent",
                  border        : "none",
                  color         : active ? "#fff" : "rgba(255,255,255,0.75)",
                  padding       : "9px 14px",
                  cursor        : "pointer",
                  fontSize      : "0.84rem",
                  fontWeight    : active ? 700 : 400,
                  whiteSpace    : "nowrap",
                  borderBottom  : active ? "2px solid #60a5fa" : "2px solid transparent",
                  transition    : "all 0.15s",
                  borderRadius  : "2px 2px 0 0",
                }}
                onMouseEnter={e => {
                  if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                }}
                onMouseLeave={e => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                {item.label}
              </button>
            );
          })}
      </nav>{/* 2행(nav) 끝 */}

    </header>
  );
}
